import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "new-password-modal__field-group" }
const _hoisted_2 = { class: "new-password-modal__field-group" }
const _hoisted_3 = {
  key: 0,
  class: "new-password-modal__invalid-link"
}
const _hoisted_4 = ["disabled"]

import { computed, reactive, ref, h, nextTick } from 'vue';
import {
  showStcErrorNotification,
} from '@/js/components/stc-notification';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import { openLoginModal } from '@/js/widgets/login-modal';

import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const CHANGE_PASSWORD_ENDPOINT = '/api/auth/change-password/';
const PASSWORD_CHANGED_SUCCESS_FALLBACK_DESCRIPTION =
  'Вы можете авторизоваться, используя новый пароль.';
  const FAIL_PASSWORD_RECOVER_FALLBACK =
  'Ссылка не действительна. Перейдите по актуальной ссылке на почте для смены пароля.';
type PasswordResetUrlParams = {
  userCheckword: string;
  userLogin: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'new-password-modal',
  setup(__props, { expose: __expose }) {

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const isOpen = ref(false);
const submitLoading = ref(false);
const passwordChangedSuccessModalOpen = ref(false);
const passwordChangedSuccessDescription = ref('');
const isWrongCheckWord = ref(false);
const formRef = ref<FormInstance>();

const form = reactive({
  password: '',
  confirmPassword: '',
});

const rules: Record<string, Rule[]> = {
  password: [{ required: true, message: 'Введите новый пароль' }],
  confirmPassword: [{ required: true, message: 'Подтвердите пароль' }],
};

const isStrongPassword = (value: string): boolean => {
  const hasLength = value.length >= 6 && value.length <= 10;
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  return hasLength && hasUppercase && hasLowercase && hasDigit;
};

const isPasswordStrong = computed(() => isStrongPassword(form.password));
const isConfirmPasswordStrong = computed(() => isStrongPassword(form.confirmPassword));

const showPasswordStrengthHint = computed(() => form.password.trim().length > 0);
const showPasswordsMatchHint = computed(
  () => form.password.trim().length > 0 && form.confirmPassword.trim().length > 0,
);

const isPasswordsMatch = computed(
  () => showPasswordsMatchHint.value && form.password === form.confirmPassword,
);

const passwordStrengthText = computed(() =>
  isPasswordStrong.value ? 'Сильный пароль' : 'Слабый пароль',
);
const passwordsMatchText = computed(() =>
  isPasswordsMatch.value ? 'Пароли совпадают' : 'Пароли не совпадают',
);

const parsePasswordResetUrlParams = (): PasswordResetUrlParams | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const userCheckword = searchParams.get('USER_CHECKWORD');
  const userLogin = searchParams.get('USER_LOGIN');

  if (!userCheckword || !userLogin) return null;

  return {
    userCheckword,
    userLogin,
  };
};

const resetUrlParams = computed(() => parsePasswordResetUrlParams());

const canSubmit = computed(
  () =>
    resetUrlParams.value !== null &&
    isPasswordStrong.value &&
    isConfirmPasswordStrong.value &&
    isPasswordsMatch.value &&
    !isWrongCheckWord.value,
);

const readApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const candidates = ['message', 'detail', 'error', 'msg'];
  for (const key of candidates) {
    const value = record[key] as {code?: Number, message?: string} | string;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    else if (typeof value !== 'string' && key === 'msg' && value?.code === 401) {
      return FAIL_PASSWORD_RECOVER_FALLBACK;
    }
    else if (typeof value !== 'string' && key === 'msg' && value?.message?.trim() ) {
      return value.message.trim();
    }
  }
  if (Array.isArray(record.detail) && record.detail.every((item) => typeof item === 'string')) {
    return record.detail.filter(Boolean).join(' ');
  }
  return null;
};

const readChangePasswordSuccessMsg = (data: unknown): string => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const msg = (data as Record<string, unknown>).msg as {mess?: string, redirectTo?: string};
  return typeof msg?.mess === 'string' && msg?.mess.trim() ? msg?.mess.trim() : '';
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const onSubmit = async () => {
  const resetParams = resetUrlParams.value;
  if (!resetParams) {
    showStcErrorNotification('Ссылка для смены пароля недействительна или устарела');
    return;
  }

  submitLoading.value = true;
  try {
    const response = await fetch(getApiUrl(CHANGE_PASSWORD_ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        login: resetParams.userLogin,
        check_word: resetParams.userCheckword,
        password: form.password,
        confirm_password: form.confirmPassword,
      }),
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      const fromBody = readApiErrorMessage(data);
      isWrongCheckWord.value = true;

      throw new Error(fromBody ?? `Ошибка смены пароля (${response.status})`);
    }

    passwordChangedSuccessDescription.value =
      readChangePasswordSuccessMsg(data) || PASSWORD_CHANGED_SUCCESS_FALLBACK_DESCRIPTION;
    trackYaGoal('register');
    close();
    passwordChangedSuccessModalOpen.value = true;
  } catch (err) {
    showStcErrorNotification(err instanceof Error ? err.message : 'Не удалось сохранить пароль');
  } finally {
    submitLoading.value = false;
  }
};

const resetFormAndHideMainModal = () => {
  form.password = '';
  form.confirmPassword = '';
  isOpen.value = false;
};

const onPasswordChangedSuccessModalOpenUpdate = (open: boolean) => {
  passwordChangedSuccessModalOpen.value = open;
  if (!open) {
    passwordChangedSuccessDescription.value = '';
    resetFormAndHideMainModal();
  }
};

const handleContinue = () => {
  nextTick(() => {
    openLoginModal();
  });
};

const open = () => {
  isOpen.value = true;
};

const close = () => {
  passwordChangedSuccessModalOpen.value = false;
  resetFormAndHideMainModal();
};

const renderPasswordIcon = (visible: boolean) =>
  h('img', {
    src: visible ? `${ICONS_BASE}/share-icons/eye-slash.svg` : `${ICONS_BASE}/share-icons/eye.svg`,
    alt: '',
    width: 24,
    height: 24,
  });

__expose({ open, close });

return (_ctx: any,_cache: any) => {
  const _component_a_input_password = _resolveComponent("a-input-password")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_alert = _resolveComponent("a-alert")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_modal, {
      open: isOpen.value,
      "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((isOpen).value = $event)),
      class: "new-password-modal",
      closable: true,
      "mask-closable": false,
      keyboard: false,
      footer: null,
      onCancel: close,
      centered: "",
      "wrap-class-name": "full-modal-new-password"
    }, {
      title: _withCtx(() => [...(_cache[3] || (_cache[3] = [
        _createElementVNode("div", { class: "new-password-modal__header" }, [
          _createElementVNode("span", { class: "new-password-modal__title" }, "Новый пароль"),
          _createElementVNode("span", { class: "new-password-modal__description" }, [
            _createTextVNode(" Придумайте пароль из 6-10 символов. "),
            _createElementVNode("br"),
            _createTextVNode(" Используйте цифры, заглавные и строчные буквы. ")
          ])
        ], -1)
      ]))]),
      default: _withCtx(() => [
        _createVNode(_component_a_form, {
          ref_key: "formRef",
          ref: formRef,
          class: "new-password-modal__form stc-ant-field-skin",
          layout: "vertical",
          "required-mark": false,
          model: form,
          rules: rules,
          onFinish: onSubmit
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(_component_a_form_item, {
                name: "password",
                label: "Новый пароль",
                class: "stc-ant-field-skin",
                "validate-status": showPasswordStrengthHint.value && !isPasswordStrong.value ? 'error' : undefined
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_input_password, {
                    value: form.password,
                    "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.password) = $event)),
                    size: "large",
                    placeholder: "Введите новый пароль",
                    autocomplete: "new-password",
                    "icon-render": renderPasswordIcon
                  }, null, 8, ["value"])
                ]),
                _: 1
              }, 8, ["validate-status"]),
              (showPasswordStrengthHint.value)
                ? (_openBlock(), _createBlock(_component_a_alert, {
                    key: 0,
                    class: "new-password-modal__hint",
                    type: isPasswordStrong.value ? 'success' : 'error',
                    message: passwordStrengthText.value,
                    "show-icon": ""
                  }, null, 8, ["type", "message"]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_a_form_item, {
                name: "confirmPassword",
                label: "Подтвердить пароль",
                class: "stc-ant-field-skin",
                "validate-status": showPasswordsMatchHint.value && !isPasswordsMatch.value ? 'error' : undefined
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_input_password, {
                    value: form.confirmPassword,
                    "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.confirmPassword) = $event)),
                    size: "large",
                    placeholder: "Подтвердите пароль",
                    autocomplete: "new-password",
                    "icon-render": renderPasswordIcon
                  }, null, 8, ["value"])
                ]),
                _: 1
              }, 8, ["validate-status"]),
              (showPasswordsMatchHint.value)
                ? (_openBlock(), _createBlock(_component_a_alert, {
                    key: 0,
                    class: "new-password-modal__hint",
                    type: isPasswordsMatch.value ? 'success' : 'error',
                    message: passwordsMatchText.value,
                    "show-icon": ""
                  }, null, 8, ["type", "message"]))
                : _createCommentVNode("", true)
            ]),
            (!resetUrlParams.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_3, " Ссылка для смены пароля недействительна или устарела. Запросите новую со страницы входа. "))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              type: "submit",
              class: "btn btn-primary btn-medium new-password-modal__submit",
              disabled: !canSubmit.value || submitLoading.value
            }, " Сохранить ", 8, _hoisted_4)
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(ConsultationRequestSuccessModal, {
      open: passwordChangedSuccessModalOpen.value,
      title: "Пароль изменен",
      description: passwordChangedSuccessDescription.value,
      "onUpdate:open": onPasswordChangedSuccessModalOpenUpdate,
      "on-continue": handleContinue
    }, null, 8, ["open", "description"])
  ], 64))
}
}

})